
/* eslint-disable vue/no-mutating-props */
import Vue from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
	OnlinePortalDocumentRequest,
	OnlinePortalApplicationDocumentRequest,
	DocumentRequestTypes,
	BootstrapVueSelectOption,
	OnlinePortalUser
} from '@/models';
import DocumentUploadHeader from './document-upload-header.vue';
import { DateFormatting } from '@sageworks/core-logic';
import DocumentUploadPopup from './document-upload-popup.vue';
import { ApplicationDocumentRequest } from '@sageworks/jpi';
import { FileHelper } from '@sageworks/core-logic';

export default Vue.extend({
	name: 'DocumentUpload',
	components: { DocumentUploadPopup, FontAwesomeIcon, DocumentUploadHeader },
	props: {
		documentRequest: [OnlinePortalApplicationDocumentRequest, OnlinePortalDocumentRequest],
		potentialMatchCount: {
			type: Number,
			default: 0
		}
	},
	data() {
		return {
			uploadedFile: null as File | null,
			uploadInProgress: false,
			showDocumentUploadPopup: false,
			downloadInitiated: false,
			downloadFailed: false,
			downloadFeedbackMessage: '',
			documentNameOptions: [] as BootstrapVueSelectOption[],
			additionalDocumentName: '', // Needs to be a separate variable so the password box will re-render on document name selection.
			documentNamesId: null as number | null,
			internalDocumentRequest:
				this.documentRequest instanceof OnlinePortalApplicationDocumentRequest
					? new OnlinePortalApplicationDocumentRequest({ ...this.documentRequest.request }, this.documentRequest.possibleDocuments)
					: new OnlinePortalDocumentRequest({ ...this.documentRequest.request })
		};
	},
	computed: {
		currentUser(): OnlinePortalUser {
			return this.isLenderView ? this.directStore.state.User.proxyUser : this.directStore.state.User.user;
		},
		isLenderView(): boolean {
			return this.directStore.getters.User.isLender;
		},
		isApplicationDocumentRequest(): boolean {
			return this.internalDocumentRequest.documentRequestType === DocumentRequestTypes.LoanApplicationDocumentRequest;
		},
		isAdditionalDocument(): boolean {
			return !this.internalDocumentRequest.id || this.internalDocumentRequest.id < 0 || !this.internalDocumentRequest.dateRequested;
		},
		disableFileInput(): boolean {
			return Boolean(this.internalDocumentRequest.dateUploaded) || this.uploadInProgress || Boolean(this.internalDocumentRequest.dateWaived);
		},
		disableFileUploadButton(): boolean {
			return Boolean(this.internalDocumentRequest) && Boolean(this.internalDocumentRequest.documentId);
		},
		uploadedFilename(): string | undefined {
			return (this.internalDocumentRequest as ApplicationDocumentRequest).uploadedDocumentFilename;
		},
		selectedDocumentNamesId: {
			get: function(): Number {
				return this.internalDocumentRequest?.documentNamesId ?? -1;
			},
			set: function(newValue: Number) {
				this.selectDocumentName(newValue);
			}
		}
	},
	watch: {
		documentRequest: {
			handler(newVal) {
				this.internalDocumentRequest =
					newVal instanceof OnlinePortalApplicationDocumentRequest
						? new OnlinePortalApplicationDocumentRequest({ ...newVal.request })
						: new OnlinePortalDocumentRequest({ ...newVal.request });
			},
			deep: true
		}
	},
	async beforeMount() {
		const documentNames = this.directStore.getters.DocumentName.documentNameOptionsForOtherDocuments(this.isLenderView);
		this.documentNameOptions = [
			{
				value: -1 as Number,
				text: 'Select a document name',
				disabled: true
			} as BootstrapVueSelectOption,
			...documentNames
		];
	},
	methods: {
		async selectDocumentName(newValue: any) {
			this.internalDocumentRequest.documentNamesId = newValue;
			var documentNamesId = this.internalDocumentRequest?.documentNamesId;
			if (!documentNamesId) return;
			let names = this.documentNameOptions.filter(function(option) {
				return option.value === documentNamesId;
			});
			if (names.length === 0) return;
			this.internalDocumentRequest.documentName = this.additionalDocumentName = names[0]?.text;
			if (this.isLenderView) {
				const tempRequestId = this.internalDocumentRequest.id;
				await this.createDocumentRequest();
				await this.directStore.dispatch.ApplicationDocumentRequest.updateApplicationDocumentRequestById({
					id: tempRequestId,
					request: this.internalDocumentRequest
				});
				await this.directStore.dispatch.ApplicationDocumentRequest.getApplicationDocumentRequestById(this.internalDocumentRequest.request?.id);
			}
		},
		triggerClickOnFileInput(): void {
			// Triggers the click event on the input inside b-form-file, since the wrapper isn't clickable
			((this.$refs.fileInput as Vue).$el.firstElementChild as HTMLElement).click();
		},
		onSelectedFileChange(): void {
			this.showDocumentUploadPopup = true;
		},
		async createDocumentRequest(): Promise<void> {
			// Assign folder id given the selected document name
			this.internalDocumentRequest.id = undefined;

			let documentName = null;
			if (this.internalDocumentRequest.documentNamesId) {
				documentName = this.directStore.getters.DocumentName.documentNameById(this.internalDocumentRequest.documentNamesId);
			}
			this.internalDocumentRequest.documentName = documentName?.name ?? 'Document';
			this.internalDocumentRequest.documentFolderId = documentName?.folderId ?? undefined;
			try {
				var newDocumentRequest = await this.directStore.dispatch.ApplicationDocumentRequest.createApplicationDocumentRequest(
					this.internalDocumentRequest as OnlinePortalApplicationDocumentRequest
				);
				this.internalDocumentRequest = newDocumentRequest;
				return Promise.resolve();
			} catch (err) {
				return Promise.reject(err);
			}
		},
		async updateDocumentRequest(): Promise<void> {
			this.internalDocumentRequest.dateUploaded = DateFormatting.formatDateForJpi(new Date());
			this.internalDocumentRequest.documentId = this.directStore.state.Document.currentDocument!.id;
			(this.internalDocumentRequest as ApplicationDocumentRequest).uploadedByUserType = this.isLenderView
				? ApplicationDocumentRequest.UploadedByUserTypeEnum.User
				: ApplicationDocumentRequest.UploadedByUserTypeEnum.BankCustomerUser;
			(this.internalDocumentRequest as ApplicationDocumentRequest).uploadedByUsersId = this.currentUser.id;
			this.internalDocumentRequest.documentFolderId = this.directStore.state.Document.currentDocument!.documentFolderId || undefined;
			(this.internalDocumentRequest as ApplicationDocumentRequest).uploadedDocumentFilename =
				this.directStore.state.Document.currentDocument?.fileName ?? '';
			(this.internalDocumentRequest as ApplicationDocumentRequest).notUploadedReason = null;
			try {
				if (this.isApplicationDocumentRequest) {
					await this.directStore.dispatch.ApplicationDocumentRequest.updateApplicationDocumentRequest(
						this.internalDocumentRequest as OnlinePortalApplicationDocumentRequest
					);
				} else {
					await this.directStore.dispatch.DocumentRequest.updateDocumentRequest(this.internalDocumentRequest as OnlinePortalDocumentRequest);
				}
				return Promise.resolve(this.internalDocumentRequest);
			} catch (err) {
				return Promise.reject(err);
			}
		},
		resetDocumentRequest() {
			this.internalDocumentRequest.dateUploaded = undefined;
			this.internalDocumentRequest.documentId = undefined;
			(this.internalDocumentRequest as ApplicationDocumentRequest).uploadedDocumentFilename = undefined;
		},
		validateFileUpload(): boolean {
			if (this.isAdditionalDocument && !this.internalDocumentRequest.documentNamesId) {
				this.showToast('Document Name Required', 'Select a document name to upload.', 'danger');
				return false;
			}
			return true;
		},
		// eslint-disable-next-line max-lines-per-function
		async uploadFile(password: string): Promise<void> {
			if (!this.validateFileUpload()) return;

			try {
				this.uploadInProgress = true;
				if (this.isAdditionalDocument && (!this.internalDocumentRequest.id || this.internalDocumentRequest.id < 0)) {
					await this.createDocumentRequest();
					this.showDocumentUploadPopup = true;
				}

				// Always attempt retrieve the documentName by documentNamesId, so we hopefully won't upload documents under the CustomerFacingName
				// Fallback to the documentName set on the request, which likely will be the CFN
				const uploadedDocumentName: string =
					this.directStore.getters.DocumentName.documentNameById(this.internalDocumentRequest.documentNamesId || -1)?.name ||
					this.internalDocumentRequest.documentName ||
					`Customer_Upload`;

				let uploadError: any = await this.directStore.dispatch.Document.createDocumentFromFile({
					fileToTransform: this.uploadedFile as File,
					documentFolderId: this.internalDocumentRequest.documentFolderId,
					associationCustomerId: this.internalDocumentRequest.customerId,
					proposedLoanId: this.internalDocumentRequest.proposedBankLoansId,
					requestDocumentName: uploadedDocumentName,
					requestDocumentPassword: password,
					documentNamesId: this.documentRequest.documentNamesId
				});

				if (uploadError) {
					let modelErrors = Array.isArray(uploadError.response?.modelErrors) ? uploadError.response?.modelErrors : [];
					let errorMessage = modelErrors.length > 0 ? modelErrors[0].message : null;
					await this.onUploadFailure(errorMessage);
					return;
				}
				await this.updateDocumentRequest();
				this.showDocumentUploadPopup = false;
				this.showToast('Document Upload', 'Document uploaded successfully');
			} catch (err) {
				await this.onUploadFailure();
			} finally {
				this.uploadInProgress = false;
				this.uploadedFile = null;
			}
		},
		async onUploadFailure(uploadFailureReason?: string) {
			this.showToast('Document Upload', uploadFailureReason ?? 'Document not uploaded', 'danger');
			if (this.isAdditionalDocument && this.isApplicationDocumentRequest) {
				await this.deleteApplicationDocumentRequest();
			}
			this.resetDocumentRequest();
		},
		async deleteApplicationDocumentRequest(): Promise<void> {
			return await this.directStore.dispatch.ApplicationDocumentRequest.deleteApplicationDocumentRequest(
				this.internalDocumentRequest as OnlinePortalApplicationDocumentRequest
			);
		},
		async deleteDocument(): Promise<void> {
			try {
				if (!this.isApplicationDocumentRequest) return Promise.resolve();
				if (this.isAdditionalDocument) {
					await this.deleteApplicationDocumentRequest();
				} else {
					(this.internalDocumentRequest as any).dateUploaded = null;
					(this.internalDocumentRequest as any).documentId = null;
					(this.internalDocumentRequest as any).uploadedByUserType = null;
					(this.internalDocumentRequest as any).uploadedByUsersId = null;
					(this.internalDocumentRequest as any).documentFolderId = null;
					await this.directStore.dispatch.ApplicationDocumentRequest.updateApplicationDocumentRequest(
						this.internalDocumentRequest as OnlinePortalApplicationDocumentRequest
					);
					return Promise.resolve();
				}
			} catch (err) {
				return Promise.reject(err);
			} finally {
				this.uploadedFile = null;
			}
		},
		async downloadDocument(id: number, templateName?: string): Promise<void> {
			let documentId: number = id;
			this.downloadFeedbackMessage = 'File download in progress...';
			if (this.isApplicationDocumentRequest || documentId === 0) {
				documentId = this.internalDocumentRequest.documentId || documentId;
			}
			try {
				this.downloadInitiated = true;
				await this.directStore.dispatch.Document.downloadDocumentById(documentId);
				if (!this.directStore.state.Document.currentDocumentContent) {
					this.downloadFeedbackMessage = 'Download failed. Please try again';
					this.downloadFailed = true;
					return;
				}
				let documentName = templateName ?? this.internalDocumentRequest.documentName ?? '';
				const base64Content = FileHelper.getBase64FromDataUri(this.directStore.state.Document.currentDocumentContent);
				const mimeType = FileHelper.getMimeTypeFromDataUri(this.directStore.state.Document.currentDocumentContent);
				FileHelper.downloadFile(base64Content, documentName, mimeType);
			} catch (err) {
				this.downloadFeedbackMessage = 'Download failed. Please try again';
				this.downloadFailed = true;
			} finally {
				this.downloadFeedbackMessage = '';
				this.downloadFailed = false;
			}
		},
		showToast(title: string, message: string, variant?: string) {
			this.$root.$bvToast.toast(message, {
				title: title,
				solid: true,
				autoHideDelay: 3000,
				noCloseButton: true,
				toaster: 'b-toaster-bottom-right',
				variant: variant
			});
		},
		cancelFileUpload() {
			this.uploadedFile = null;
			this.showDocumentUploadPopup = false;
		}
	}
});
